import Configuration from '@/api/Configuration';
import {
  toCreateProductListRequest,
  toEffectivePortfolio,
  toProductList,
  toProductListRetailerAssignments,
  toProductLists,
  toProductListSkuIds,
  toProductListStoreAssignments,
  toUpdateProductListRequest,
} from '@/api/mappers/mapProductList';
import {
  ProductListsApi as ApiProductListsApi,
  ProductListAssignmentOperation,
  ProductListSkuMembershipOperation,
} from '@/gen/api';
import {
  EffectivePortfolio,
  ProductList,
  ProductListCreate,
  ProductListRetailerAssignments,
  ProductLists,
  ProductListSkuIds,
  ProductListStoreAssignments,
  ProductListUpdate,
} from '@/models/ProductList';
import { Status } from '@/models/Status';

export interface ProductListFilters {
  status?: Exclude<Status, 'UNKNOWN'>;
  supplierId?: string;
}

interface ProductListsApi {
  listProductLists(
    offset?: number,
    limit?: number,
    filters?: ProductListFilters,
  ): Promise<ProductLists>;
  findProductLists(
    query?: string,
    offset?: number,
    limit?: number,
    filters?: ProductListFilters,
  ): Promise<ProductLists>;
  createProductList(productList: ProductListCreate): Promise<ProductList>;
  getProductList(id: string): Promise<ProductList>;
  updateProductList(id: string, productList: ProductListUpdate): Promise<void>;
  deleteProductList(id: string): Promise<void>;
  getProductListSkuIds(id: string, offset?: number, limit?: number): Promise<ProductListSkuIds>;
  addSkusToProductList(id: string, skuIds: string[]): Promise<void>;
  removeSkusFromProductList(id: string, skuIds: string[]): Promise<void>;
  getProductListRetailerAssignments(
    id: string,
    offset?: number,
    limit?: number,
    status?: Status,
  ): Promise<ProductListRetailerAssignments>;
  assignRetailersToProductList(id: string, retailerIds: string[]): Promise<void>;
  unassignRetailersFromProductList(id: string, retailerIds: string[]): Promise<void>;
  getProductListStoreAssignments(
    id: string,
    offset?: number,
    limit?: number,
    status?: Status,
  ): Promise<ProductListStoreAssignments>;
  assignStoresToProductList(id: string, storeIds: string[]): Promise<void>;
  unassignStoresFromProductList(id: string, storeIds: string[]): Promise<void>;
  getEffectivePortfolio(storeId: string, supplierId?: string): Promise<EffectivePortfolio>;
}

export class ProductListsApiImpl implements ProductListsApi {
  private productListsApi: ApiProductListsApi;

  constructor(configuration: Configuration, productListsApi?: ApiProductListsApi) {
    this.productListsApi = productListsApi ?? new ApiProductListsApi(configuration);
  }

  async listProductLists(
    offset = 0,
    limit = 10,
    filters?: ProductListFilters,
  ): Promise<ProductLists> {
    const response = await this.productListsApi.listProductLists({
      status: filters?.status,
      supplierId: filters?.supplierId,
      offset,
      limit,
    });

    return toProductLists(response);
  }

  async findProductLists(
    query?: string,
    offset = 0,
    limit = 10,
    filters?: ProductListFilters,
  ): Promise<ProductLists> {
    const response = await this.productListsApi.listProductLists({
      query,
      status: filters?.status,
      supplierId: filters?.supplierId,
      offset,
      limit,
    });

    return toProductLists(response);
  }

  async createProductList(productList: ProductListCreate): Promise<ProductList> {
    const response = await this.productListsApi.createProductList({
      productListCreateRequest: toCreateProductListRequest(productList),
    });

    return toProductList(response);
  }

  async getProductList(id: string): Promise<ProductList> {
    const response = await this.productListsApi.getProductList({ id });
    return toProductList(response);
  }

  async updateProductList(id: string, productList: ProductListUpdate): Promise<void> {
    await this.productListsApi.updateProductList({
      id,
      productListUpdateRequest: toUpdateProductListRequest(productList),
    });
  }

  async deleteProductList(id: string): Promise<void> {
    await this.productListsApi.deleteProductList({ id });
  }

  async getProductListSkuIds(id: string, offset = 0, limit = 10): Promise<ProductListSkuIds> {
    const response = await this.productListsApi.getProductListSkuIds({ id, offset, limit });
    return toProductListSkuIds(response);
  }

  async addSkusToProductList(id: string, skuIds: string[]): Promise<void> {
    await this.updateSkuMembership(id, skuIds, ProductListSkuMembershipOperation.Add);
  }

  async removeSkusFromProductList(id: string, skuIds: string[]): Promise<void> {
    await this.updateSkuMembership(id, skuIds, ProductListSkuMembershipOperation.Remove);
  }

  private async updateSkuMembership(
    id: string,
    skuIds: string[],
    operation: ProductListSkuMembershipOperation,
  ): Promise<void> {
    await this.productListsApi.updateProductListSkuMembership({
      id,
      addOrRemoveSkusToProductList: { skuIds, operation },
    });
  }

  async getProductListRetailerAssignments(
    id: string,
    offset = 0,
    limit = 10,
    status?: Status,
  ): Promise<ProductListRetailerAssignments> {
    const response = await this.productListsApi.getProductListRetailerAssignments({
      id,
      offset,
      limit,
      status,
    });

    return toProductListRetailerAssignments(response);
  }

  async assignRetailersToProductList(id: string, retailerIds: string[]): Promise<void> {
    await this.updateRetailerAssignments(id, retailerIds, ProductListAssignmentOperation.Assign);
  }

  async unassignRetailersFromProductList(id: string, retailerIds: string[]): Promise<void> {
    await this.updateRetailerAssignments(id, retailerIds, ProductListAssignmentOperation.Unassign);
  }

  private async updateRetailerAssignments(
    id: string,
    retailerIds: string[],
    operation: ProductListAssignmentOperation,
  ): Promise<void> {
    await this.productListsApi.updateProductListRetailerAssignments({
      id,
      assignOrUnassignRetailersToProductList: { retailerIds, operation },
    });
  }

  async getProductListStoreAssignments(
    id: string,
    offset = 0,
    limit = 10,
    status?: Status,
  ): Promise<ProductListStoreAssignments> {
    const response = await this.productListsApi.getProductListStoreAssignments({
      id,
      offset,
      limit,
      status,
    });

    return toProductListStoreAssignments(response);
  }

  async assignStoresToProductList(id: string, storeIds: string[]): Promise<void> {
    await this.updateStoreAssignments(id, storeIds, ProductListAssignmentOperation.Assign);
  }

  async unassignStoresFromProductList(id: string, storeIds: string[]): Promise<void> {
    await this.updateStoreAssignments(id, storeIds, ProductListAssignmentOperation.Unassign);
  }

  private async updateStoreAssignments(
    id: string,
    storeIds: string[],
    operation: ProductListAssignmentOperation,
  ): Promise<void> {
    await this.productListsApi.updateProductListStoreAssignments({
      id,
      assignOrUnassignStoresToProductList: { storeIds, operation },
    });
  }

  async getEffectivePortfolio(storeId: string, supplierId?: string): Promise<EffectivePortfolio> {
    const response = await this.productListsApi.getEffectivePortfolio({ storeId, supplierId });
    return toEffectivePortfolio(response);
  }
}

export default ProductListsApi;
